/**
 * SRP GAM key/value targeting, ported from the legacy SRP
 * (`apps/hotpads-web/srcLegacy/shared/modules/AdvertisementWrapper`). Without
 * these keys GAM can't match a line item to the search, so every slot falls
 * back to remnant inventory.
 */

import { GENERAL_AD_LISTING_TYPE } from './adListingType';

export type SrpAdTargeting = Record<string, string | Array<string>>;

/** The subset of `/v2/analytics/adtags` keys legacy applies to SRP slots. */
export type SrpAdTagAttributes = {
  dma?: string;
  cnty?: string;
  state?: string;
  prange?: string;
};

type AdTargetingRange = {
  min?: number | null;
  max?: number | null;
};

export type SrpAdTargetingInput = {
  area?: {
    city?: string;
    state?: string;
    coordinates?: { lat?: number; lon?: number };
  };
  price?: AdTargetingRange;
  beds?: AdTargetingRange;
  baths?: AdTargetingRange;
  zips?: ReadonlyArray<string>;
  adTags?: SrpAdTagAttributes;
  /** True when the search is clipped to the area boundary rather than the raw viewport. */
  isAreaBorderActive?: boolean;
  /** The `pis` page-view count for this session. */
  pis?: string;
  userToken?: string;
  advertisementEnv?: string;
  /** The `Rent_*` segment from `getAdListingType`; must match the ad unit path. */
  adListingType?: string;
};

const AD_TARGETING_VERSION = 'hp_search_control';

// ADVERTISEMENT_ENV values that target GAM's stage inventory (legacy's `useStageForTargeting`).
const STAGE_ADVERTISEMENT_ENVS = new Set(['local', 'comet1', 'sherman', 'feeds', 'node-int']);

// GAM's bed/bath vocabularies, from legacy FilterConstants. `8plus` is the open-ended top value.
const OPEN_ENDED_VALUE = '8plus';
const BED_TARGETING_VALUES = ['0', '1', '2', '3', '4', '5', '6', '7', OPEN_ENDED_VALUE];
const BATH_TARGETING_VALUES = [
  '0',
  '0.5',
  '1',
  '1.5',
  '2',
  '2.5',
  '3',
  '3.5',
  '4',
  '4.5',
  '5',
  '5.5',
  '6',
  '6.5',
  '7',
  '7.5',
  OPEN_ENDED_VALUE,
];

const PRICE_BUCKETS: ReadonlyArray<[number, number]> = [
  [0, 499],
  [500, 749],
  [750, 999],
  [1000, 1249],
  [1250, 1499],
  [1500, 1749],
  [1750, 1999],
  [2000, 2499],
  [2500, 2999],
  [3000, 3499],
  [3500, 3999],
  [4000, 4499],
  [4500, 4999],
  [5000, 5999],
  [6000, 6999],
  [7000, 7999],
  [8000, 8999],
  [9000, 9999],
];
const ABOVE_TOP_PRICE_BUCKET = '10000_and_up';

export function getAdTargetingEnv(
  advertisementEnv = process.env.NEXT_PUBLIC_ADVERTISEMENT_ENV,
): 'production' | 'stage' {
  return advertisementEnv && STAGE_ADVERTISEMENT_ENVS.has(advertisementEnv) ? 'stage' : 'production';
}

/** Every GAM price bucket the [low, high] range overlaps, e.g. `1000-1249,1250-1499`. */
export function getPriceRangeTargeting(lowPrice?: number | null, highPrice?: number | null): string {
  const low = lowPrice ?? 0;
  const high = highPrice ?? Number.POSITIVE_INFINITY;
  const buckets = PRICE_BUCKETS.filter(([min, max]) => low <= max && high >= min).map(
    ([min, max]) => `${min}-${max}`,
  );

  if (high > PRICE_BUCKETS[PRICE_BUCKETS.length - 1][1]) buckets.push(ABOVE_TOP_PRICE_BUCKET);

  return buckets.join(',');
}

/** Legacy's non-secure DJB2 hash (`genericUtils.hash`) — GAM keys off the same value. */
export function hashUserToken(userToken: string): string {
  let hash = 5381;
  for (let i = 0; i < userToken.length; i++) {
    hash = (hash << 5) + hash + userToken.charCodeAt(i);
  }
  return String(hash);
}

function toTargetingToken(value: string): string {
  return value.toLowerCase().split(' ').join('_');
}

function toNumericValue(value: string): number {
  return value === OPEN_ENDED_VALUE ? Number.POSITIVE_INFINITY : Number(value);
}

function rangeTargetingValues(values: Array<string>, range?: AdTargetingRange): Array<string> {
  if (range?.min == null && range?.max == null) return [];

  const min = range?.min ?? Number.NEGATIVE_INFINITY;
  const max = range?.max ?? Number.POSITIVE_INFINITY;
  return values.filter((value) => {
    const numeric = toNumericValue(value);
    return numeric >= min && numeric <= max;
  });
}

function resolveStateTargeting({
  areaState,
  adTagsState,
  isAreaBorderActive,
}: {
  areaState?: string;
  adTagsState?: string;
  isAreaBorderActive: boolean;
}): string | undefined {
  // Oregon's GAM value is `ore`, not the `or` the area API returns.
  const normalized = areaState?.toLowerCase() === 'or' ? 'ore' : areaState?.toLowerCase();

  // A boundary-clipped search is exactly the area, so its state beats the one
  // adtags derives from the viewport.
  if (isAreaBorderActive && normalized) return normalized;

  return adTagsState ?? normalized;
}

export function buildSrpAdTargeting(input: SrpAdTargetingInput): SrpAdTargeting {
  const {
    area,
    price,
    beds,
    baths,
    zips = [],
    adTags,
    isAreaBorderActive = false,
    pis,
    userToken = '',
    advertisementEnv,
    adListingType = GENERAL_AD_LISTING_TYPE,
  } = input;

  const draft: Record<string, string | Array<string> | undefined> = {
    city: area?.city ? [toTargetingToken(area.city)] : undefined,
    state: resolveStateTargeting({
      areaState: area?.state,
      adTagsState: adTags?.state,
      isAreaBorderActive,
    }),
    zip: Array.from(new Set(zips)),
    latitude: area?.coordinates?.lat == null ? undefined : String(area.coordinates.lat),
    longitude: area?.coordinates?.lon == null ? undefined : String(area.coordinates.lon),
    // adtags derives its own buckets from the same bounds and price, so let it win.
    prange: adTags?.prange ?? getPriceRangeTargeting(price?.min, price?.max),
    bd: rangeTargetingValues(BED_TARGETING_VALUES, beds),
    ba: rangeTargetingValues(BATH_TARGETING_VALUES, baths),
    dma: adTags?.dma,
    cnty: adTags?.cnty,
    listtp: adListingType,
    pis,
    env: getAdTargetingEnv(advertisementEnv),
    vers: AD_TARGETING_VERSION,
    zguidh: hashUserToken(userToken),
  };

  const targeting: SrpAdTargeting = {};
  for (const [key, value] of Object.entries(draft)) {
    if (value === undefined || value.length === 0) continue;
    targeting[key] = value;
  }
  return targeting;
}
